import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DiscrepancyHeader } from './components/DiscrepancyHeader';
import { InventoryItemsGrid } from '../../components/InventoryItemsGrid/InventoryItemsGrid';
import { GoBackConfirmationModal } from './components/GoBackConfirmationModal';
import { ConfirmInventoryModal } from './components/ConfirmInventoryModal';
import { MarkAsResolvedModal } from './components/MarkAsResolvedModal';
import { useManageDiscrepancies } from './useManageDiscrepancies';
import {
  useEndManagingInventoryRequest,
  useConfirmInventoryRequest,
  useSendForReviewInventoryRequest,
  useRejectInventoryRequest
} from '../../hooks/useApi';
import './index.css';

const ManageDiscrepancies = () =>
{
  const navigate = useNavigate();
  const [showGoBackModal, setShowGoBackModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showMarkResolvedModal, setShowMarkResolvedModal] = useState(false);
  const [selectedResolveItemId, setSelectedResolveItemId] = useState(null);

  const {
    inventoryId,
    posId,
    items,
    total,
    issues,
    resolved,
    remaining,
    loading,
    error,
    isHqUser,
    inventoryStatus,
    inReview,
    markItemResolved,
    resolveAllItems,
    updateItemActual,
    getApiItems,
  } = useManageDiscrepancies();

  const [{}, endManagingInventory] = useEndManagingInventoryRequest(posId);
  const [{loading: confirmLoading}, confirmInventory] = useConfirmInventoryRequest(posId);
  const [{loading: reviewLoading}, sendForReview] = useSendForReviewInventoryRequest(posId);
  const [{loading: rejectLoading}, rejectInventory] = useRejectInventoryRequest(posId);

  // End managing on unmount
  useEffect(() =>
  {
    return () =>
    {
      if (inventoryId && posId)
      {
        endManagingInventory({
          bodyParams: { inventoryId: parseInt(inventoryId, 10) }
        }).catch(err =>
        {
          console.error('Error ending inventory management:', err);
        });
      }
    };
  }, [inventoryId, posId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBack = () =>
  {
    setShowGoBackModal(true);
  };

  const handleConfirmGoBack = () =>
  {
    endManagingInventory({
      bodyParams: { inventoryId: parseInt(inventoryId, 10) }
    }).finally(() =>
    {
      navigate(`/inventory-management/${inventoryId}`);
    });
  };

  // Mark as resolved flow
  const handleMarkResolved = (item) =>
  {
    setSelectedResolveItemId(item.itemId);
    setShowMarkResolvedModal(true);
  };

  const handleConfirmMarkResolved = () =>
  {
    if (selectedResolveItemId)
    {
      markItemResolved(selectedResolveItemId);
    }
    setShowMarkResolvedModal(false);
    setSelectedResolveItemId(null);
  };

  // Edit actual flow
  const getReasonCodeForDifference = (difference) =>
  {
    if (difference > 0) return 'POSITIVE_INVENTORY_MOVEMENT';
    if (difference < 0) return 'NEGATIVE_INVENTORY_MOVEMENT';
    return null;
  };

  const handleEditActual = (itemId, newActualValue, expectedValue) =>
  {
    updateItemActual(itemId, newActualValue);

    console.log(
      'Actual quantity updated. Item auto-resolved with reason: SCAN_QUANTITY_ADJUSTED'
    );
  };

  // Resolve all
  const handleResolveAll = () =>
  {
    resolveAllItems();
  };

  // Send to review / Confirm (HQ)
  const handleConfirmInventory = () =>
  {
    setShowConfirmModal(true);
  };

  const handleConfirmInventorySubmit = () =>
  {
    const apiItems = getApiItems();
    const bodyParams = {
      inventoryId: parseInt(inventoryId, 10),
      inventoryItems: apiItems,
    };

    const apiCall = inReview ? confirmInventory : sendForReview;

    apiCall({ bodyParams })
      .then(() =>
      {
        setShowConfirmModal(false);
        navigate(`/inventory-management/${inventoryId}`);
      })
      .catch(err =>
      {
        console.error('Error submitting inventory:', err);
      });
  };

  // Reject (HQ only)
  const handleRejectInventory = () =>
  {
    setShowRejectModal(true);
  };

  const handleRejectInventorySubmit = () =>
  {
    rejectInventory({
      bodyParams: { inventoryId: parseInt(inventoryId, 10) }
    })
      .then(() =>
      {
        setShowRejectModal(false);
        navigate(`/inventory-management/${inventoryId}`);
      })
      .catch(err =>
      {
        console.error('Error rejecting inventory:', err);
      });
  };

  const actionLoading = confirmLoading || reviewLoading;

  return (
    <div className="manage-disc-page">
      <DiscrepancyHeader
        total={total}
        issues={issues}
        resolved={resolved}
        remaining={remaining}
        onConfirm={handleConfirmInventory}
        onReject={handleRejectInventory}
        onBack={handleBack}
        confirmDisabled={!inReview && remaining > 0}
        inReview={inReview}
        isHqUser={isHqUser}
      />
      <InventoryItemsGrid
        items={items}
        loading={loading}
        error={error}
        onMarkResolved={handleMarkResolved}
        onEditActual={handleEditActual}
        onResolveAll={handleResolveAll}
        inventoryStatus={inventoryStatus}
      />

      <GoBackConfirmationModal
        isOpen={showGoBackModal}
        onClose={() => setShowGoBackModal(false)}
        onConfirm={handleConfirmGoBack}
      />

      <MarkAsResolvedModal
        isOpen={showMarkResolvedModal}
        onClose={() => { setShowMarkResolvedModal(false); setSelectedResolveItemId(null); }}
        onConfirm={handleConfirmMarkResolved}
      />

      <ConfirmInventoryModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmInventorySubmit}
        loading={actionLoading}
        title={inReview ? 'Confirm Inventory' : 'Send To Review'}
        message={inReview
          ? 'Are you sure you want to confirm this inventory? This action cannot be undone.'
          : 'Are you sure you want to send this inventory for review?'
        }
        confirmText={inReview ? 'Yes, Confirm' : 'Yes, Send'}
        loadingText={inReview ? 'Confirming...' : 'Sending...'}
      />

      <ConfirmInventoryModal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        onConfirm={handleRejectInventorySubmit}
        loading={rejectLoading}
        title="Reject Inventory"
        message="Are you sure you want to reject this inventory? It will be sent back for revision."
        confirmText="Yes, Reject"
        loadingText="Rejecting..."
      />
    </div>
  );
};

export default ManageDiscrepancies;
